/* eslint-disable import/no-unresolved */
import BaseElement from 'base-element'
import { css, html, ref, createRef, repeat } from 'lit-all'
import { StorefrontClient } from 'lib-storefront'
import styles from 'lib-styles'
import * as paths from 'lib-paths'
import { handleize } from 'lib-utils'
import { BazaarvoiceClient } from 'lib-vendor'
/* eslint-disable import/no-unresolved */

class HHCollectionGrid extends BaseElement {
  static properties = {
    _loading: { type: Boolean, state: true },
    _currentPage: { type: Number, state: true },
    _sortBy: { type: String, state: true },
    _filters: { type: Array, state: true },
    collectionId: {
      type: String,
      attribute: 'collection-id',
    },
    title: { type: String },
    strategy: { type: String }, // 'default', 'variant', 'product', or 'metafield-variants'
    productsPerPage: { type: Number, attribute: 'products-per-page' },
    products: { type: Array, state: true },
    showingFilters: {
      type: Boolean,
      reflect: true,
      attribute: 'showing-filters',
    },
  }

  $filters = createRef()

  $actions = createRef()

  get filteredProducts() {
    if (!this.products?.length) return []
    const filteredProducts = this._filterProducts(this.products)
    document.dispatchEvent(
      new CustomEvent('plp:update', {
        detail: {
          productsCount: filteredProducts.length,
        },
      })
    )
    return filteredProducts
  }

  get filteredBestSellingProducts() {
    if (!this.bestSellingproducts?.length) return []
    const filteredProducts = this._filterProducts(this.bestSellingproducts)

    return filteredProducts
  }

  get sortedProducts() {
    switch (this._sortBy) {
      case 'best-selling':
        return this.filteredBestSellingProducts
      case 'title-ascending':
        return this.filteredProducts.sort((a, b) => {
          if (a.title < b.title) return -1
          if (a.title > b.title) return 1
          return 0
        })
      case 'title-descending':
        return this.filteredProducts.sort((a, b) => {
          if (a.title < b.title) return 1
          if (a.title > b.title) return -1
          return 0
        })
      case 'price-ascending':
        return this.filteredProducts.sort((a, b) => {
          const priceA = parseFloat(a.variants[0].price)
          const priceB = parseFloat(b.variants[0].price)
          if (priceA < priceB) return -1
          if (priceA > priceB) return 1
          return 0
        })
      case 'price-descending':
        return this.filteredProducts.sort((a, b) => {
          const priceA = parseFloat(a.variants[0].price)
          const priceB = parseFloat(b.variants[0].price)
          if (priceA < priceB) return 1
          if (priceA > priceB) return -1
          return 0
        })
      case 'published-ascending':
        return this.filteredProducts.sort((a, b) => a.published - b.published)
      case 'published-descending':
        return this.filteredProducts.sort((a, b) => b.published - a.published)
      case 'created-ascending':
        return this.filteredProducts.sort((a, b) => a.createdAt - b.createdAt)
      case 'created-descending':
        return this.filteredProducts.sort((a, b) => b.createdAt - a.createdAt)
      case 'rating':
        return this.filteredProducts.sort((a, b) => {
          const ratingA =
            (a.rating.average * a.rating.count) / (a.rating.count + 10)
          const ratingB =
            (b.rating.average * b.rating.count) / (b.rating.count + 10)
          return ratingB - ratingA
        })
      case 'saving':
        return this.filteredProducts.sort((a, b) => {
          const variantA = a.variants[0]
          const variantB = b.variants[0]
          const priceA = parseFloat(variantA.price)
          const priceB = parseFloat(variantB.price)
          const compareAtPriceA = parseFloat(variantA.compareAtPrice) || priceA
          const compareAtPriceB = parseFloat(variantB.compareAtPrice) || priceB
          const savingA = compareAtPriceA - priceA
          const savingB = compareAtPriceB - priceB
          return savingB - savingA
        })
      default: // applies for "manual"
        return this.filteredProducts
    }
  }

  get paginatedProducts() {
    const totalPages = Math.ceil(
      this.sortedProducts.length / this.productsPerPage
    )
    const paginated = []
    for (let i = 0; i < totalPages; i++) {
      const start = i * this.productsPerPage
      const end = start + this.productsPerPage
      paginated.push(this.sortedProducts.slice(start, end))
    }
    return paginated
  }

  get currentPageItems() {
    if (!this.paginatedProducts.length) return []
    return this.paginatedProducts[this._currentPage - 1]
  }

  get appliedFacets() {
    return this._filters.reduce((acc, filter) => {
      if (filter.facet === 'price-min') {
        acc.push({ filter, text: `Min price: $${filter.value}` })
      } else if (filter.facet === 'price-max') {
        acc.push({ filter, text: `Max price: $${filter.value}` })
      } else {
        filter.value.forEach((_) =>
          acc.push({ filter: { facet: filter.facet, value: _ }, text: _ })
        )
      }
      return acc
    }, [])
  }

  get filterCountText() {
    if (!this.appliedFacets.length) return html``
    return html`(${this.appliedFacets.length})`
  }

  get normalizedStrategy() {
    return handleize(this.strategy)
  }

  get header() {
    return document.querySelector('.js-header-nav')
  }

  set actionBarHeight(value) {
    if (!this.$actions.value) return
    this.style.setProperty('--action-bar-height', value)
  }

  constructor() {
    super()
    this.refresh()
    this.client = new StorefrontClient()
    this.bvClient = new BazaarvoiceClient()
    this.strategy = 'default'
    this.productsPerPage = 1000
    this.products = []
    this.bestSellingproducts = []
    this.facetTagConfigurations = []
    this._loading = true
  }

  connectedCallback() {
    super.connectedCallback()
    this._fetchData()
    this._fetchBestSellingData()
    this.header.addEventListener('header:scroll', this._handleHeaderScroll)
    document.addEventListener('sort:change', () => this.refresh())
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.header.removeEventListener('header:scroll', this._handleHeaderScroll)
  }

  firstUpdated() {
    if (this.$actions.value) {
      this.actionBarHeight = `${this.$actions?.value.offsetHeight}px`
    }
  }

  async _fetchData() {
    let collectionData
    if (this.normalizedStrategy === 'metafield-variants') {
      collectionData = await this.client.getCollectionVariantsFromMetafield(
        this.collectionId
      )
      if (collectionData?.products?.length) {
        try {
          const variants = await this.client.getVariantsByBatch(
            collectionData.products
          )
          this.products = variants
            .filter((_) => !_.metafields?.custom?.hide)
            .map((_) => {
              const productVariants = _.product.variants.filter(
                (variant) => !variant.metafields?.custom?.hide
              )
              return {
                ..._.product,
                ..._,
                minPrice: _.price,
                title: _.product.title,
                variants: productVariants,
                options: _.product.options,
                cardOptions: _.options.reduce(
                  (acc, current) => [...acc, current.value],
                  []
                ),
                parentProductId: _.product.id,
              }
            })
        } catch (error) {
          /* eslint-disable no-console */
          console.error(
            'Error fetching variant overrides from metafield data:',
            error
          )
          /* eslint-enable no-console */
        }
      }
    } else {
      collectionData = await this.client.getCollectionById(
        this.collectionId,
        250,
        this._currentPage,
        'MANUAL'
      )
      if (this.normalizedStrategy === 'variant') {
        this.products = collectionData.products.reduce(
          (accumulator, current) => {
            current.variants = current.variants.filter(
              (variant) => !variant.metafields?.custom?.hide
            )
            /* eslint-disable no-param-reassign */
            return [
              ...accumulator,
              ...current.variants.map((_) => ({
                ...current,
                price: _.price,
                minPrice: _.price,
                image: current.featuredImage,
                parentProductId: current.id,
                id: _.id, // Set initial ID to corresponding variant (ensures correct swatch is pre-selected)
                cardOptions: _.options,
              })),
            ]
            /* eslint-enable no-param-reassign */
          },
          []
        )
      } else {
        this.products = collectionData.products.map((_) => {
          const variants = _.variants.filter(
            (variant) => !variant.metafields?.custom?.hide
          )
          return {
            ..._,
            variants,
            price: _.minPrice,
            image: _.featuredImage,
            cardOptions: [],
          }
        })
      }
    }

    const filterConfigurations =
      collectionData.metafields?.custom?.filterConfiguration
    if (filterConfigurations?.length) {
      try {
        const fetchPromises = filterConfigurations.map(async (id) => {
          const response = await this.client.getMetaobjectById(id)
          return response
        })
        this.facetTagConfigurations = await Promise.all(fetchPromises)
      } catch (error) {
        /* eslint-disable no-console */
        console.error('Error fetching facet config data:', error)
        /* eslint-enable no-console */
      }
    }

    if (!this.title) {
      this.title = `Shop ${collectionData.title.replace(/^Shop/i, '').trim()}` // Remove "Shop" if it's already part of the title
    }

    await this._fetchBVStatistics(this.products)

    document.dispatchEvent(
      new CustomEvent('plp:loaded', {
        detail: {
          productsCount: this.products.length,
        },
      })
    )

    this._loading = false
  }

  async _fetchBestSellingData() {
    let collectionData
    if (this.normalizedStrategy === 'metafield-variants') {
      collectionData = await this.client.getCollectionVariantsFromMetafield(
        this.collectionId
      )
      if (collectionData?.products?.length) {
        try {
          const variants = await this.client.getVariantsByBatch(
            collectionData.products
          )
          this.bestSellingproducts = variants
            .filter((_) => !_.metafields?.custom?.hide)
            .map((_) => {
              const productVariants = _.product.variants.filter(
                (variant) => !variant.metafields?.custom?.hide
              )
              return {
                ..._.product,
                ..._,
                minPrice: _.price,
                title: _.product.title,
                variants: productVariants,
                options: _.product.options,
                cardOptions: _.options.reduce(
                  (acc, current) => [...acc, current.value],
                  []
                ),
                parentProductId: _.product.id,
              }
            })
        } catch (error) {
          /* eslint-disable no-console */
          console.error(
            'Error fetching variant overrides from metafield data:',
            error
          )
          /* eslint-enable no-console */
        }
      }
    } else {
      collectionData = await this.client.getCollectionById(
        this.collectionId,
        250,
        this._currentPage,
        'BEST_SELLING'
      )
      if (this.normalizedStrategy === 'variant') {
        this.bestSellingproducts = collectionData.products.reduce(
          (accumulator, current) => {
            current.variants = current.variants.filter(
              (variant) => !variant.metafields?.custom?.hide
            )
            /* eslint-disable no-param-reassign */
            return [
              ...accumulator,
              ...current.variants.map((_) => ({
                ...current,
                price: _.price,
                minPrice: _.price,
                image: current.featuredImage,
                parentProductId: current.id,
                id: _.id, // Set initial ID to corresponding variant (ensures correct swatch is pre-selected)
                cardOptions: _.options,
              })),
            ]
            /* eslint-enable no-param-reassign */
          },
          []
        )
      } else {
        this.bestSellingproducts = collectionData.products.map((_) => {
          const variants = _.variants.filter(
            (variant) => !variant.metafields?.custom?.hide
          )
          return {
            ..._,
            variants,
            price: _.minPrice,
            image: _.featuredImage,
            cardOptions: [],
          }
        })
      }
    }

    await this._fetchBVStatistics(this.bestSellingproducts)
  }

  async _fetchBVStatistics(products) {
    try {
      const idKey = ['variant', 'metafield-variants'].includes(
        this.normalizedStrategy
      )
        ? 'parentProductId'
        : 'id'
      const productIds = products.map((product) => product[idKey])
      const reviewsData = await this.bvClient.fetchReviews(productIds)
      const productsWithReviewStats = reviewsData.Includes.Products
      products.forEach((product) => {
        const productWithReviewStats = productsWithReviewStats[product[idKey]]
        if (productWithReviewStats) {
          const stats = productWithReviewStats.ReviewStatistics
          // eslint-disable-next-line no-param-reassign
          product.rating = {
            average: stats.AverageOverallRating,
            count: stats.TotalReviewCount,
          }
        }
      })
    } catch (err) {
      /* eslint-disable no-console */
      console.error('Error fetching product statistics:', err)
      /* eslint-enable no-console */
    }
  }

  refresh = () => {
    this._params = new URLSearchParams(window.location.search)
    this._currentPage = Number(this._params.get('page')) || 1
    this._sortBy = this._params.get('sort') || 'published-descending'
    this._filters = [
      // { facet: 'type', value: this._params.getAll('type') },
      // { facet: 'vendor', value: this._params.getAll('vendor') },
      { facet: 'color', value: this._params.getAll('color') },
      { facet: 'price-min', value: this._params.get('price-min') },
      { facet: 'price-max', value: this._params.get('price-max') },
      { facet: 'tags', value: this._params.getAll('tags') },
    ].filter((_) => _.value?.length > 0)
    this.requestUpdate()
  }

  setPage(page) {
    this._currentPage = page
    const url = new URL(window.location.href)
    url.searchParams.set('page', page)
    window.history.pushState({}, '', url)
    window.scrollTo({
      top: 100,
      behavior: 'smooth',
    })
  }

  toggleFilters = () => {
    this.actionBarHeight = `${this.$actions?.value.offsetHeight}px`
    if (this.showingFilters) {
      this.$filters.value.style.maxHeight = '0px'
      this.$filters.value.addEventListener(
        'transitionend',
        () => {
          if (this.showingFilters) {
            this.$filters.value.style.maxHeight = 'none'
          }
        },
        { once: true }
      )
      this.showingFilters = false
    } else {
      this.$filters.value.style.maxHeight = 'none'
      const height = this.$filters.value.offsetHeight
      this.$filters.value.style.maxHeight = '0px'
      requestAnimationFrame(() => {
        this.$filters.value.style.maxHeight = `${height}px`
      })
      this.showingFilters = true
    }
  }

  renderPagination() {
    if (this._loading || this.products.length <= this.productsPerPage) return ''
    return html`
      <nav id="pagination" role="navigation" aria-label="Pagination navigation">
        <button
          class="page-button page-button--prev"
          @click=${() => this.setPage(this._currentPage - 1)}
          ?hidden=${this._currentPage === 1}
        >
          ${paths.chevron}
        </button>
        ${repeat(
          this.paginatedProducts,
          (page, index) => html`
            <button
              @click=${() => this.setPage(index + 1)}
              class="page-button"
              ?active=${this._currentPage === index + 1}
              aria-current=${this._currentPage === index + 1 ? 'page' : 'false'}
              aria-label=${`Go to page ${index + 1}`}
            >
              ${index + 1}
            </button>
          `
        )}
        <button
          class="page-button page-button--next"
          @click=${() => this.setPage(this._currentPage + 1)}
          ?hidden=${this._currentPage === this.products.length}
        >
          ${paths.chevron}
        </button>
      </nav>
    `
  }

  _renderAppliedFacets() {
    return html`
      <ul id="applied-facets">
        ${this.appliedFacets.map(
          ({ filter, text }) => html`
            <li class="removable-facet">
              <button
                class="remove-facet-button"
                type="button"
                aria-label="Remove"
                @click=${() => this.removeFilter(filter)}
              >
                ${paths.close}
              </button>
              <span>${text.includes(':') ? text.split(':')[1] : text}</span>
            </li>
          `
        )}
        <li ?hidden=${!this.appliedFacets.length}>
          <button @click=${this.clearFilters} class="underline" type="button">
            Clear all
          </button>
        </li>
      </ul>
    `
  }

  _handleFiltersClick(event) {
    if (event.target === event.currentTarget) {
      this.toggleFilters()
    }
  }

  clearFilters() {
    this._filters = []
    window.history.replaceState(null, null, window.location.pathname)
  }

  _handleHeaderScroll = (e) => {
    const { direction } = e.detail
    const isScrollUp = direction === 'up'
    this.style.setProperty(
      '--action-bar-top-offset',
      isScrollUp ? 'var(--header-height)' : '0px'
    )
    this.style.setProperty(
      '--action-bar-transition-timing',
      isScrollUp ? '0.6s' : '0.1s'
    )
  }

  removeFilter(facet) {
    const url = new URL(window.location.href)
    // Get all query params set for this facet
    const paramValues = this._params.getAll(facet.facet)
    // Begin by removing them all
    this._params.delete(facet.facet)
    // Re-add all but the one we're removing
    paramValues.forEach((value) => {
      if (value !== facet.value) {
        this._params.append(facet.facet, value)
      }
    })
    url.search = this._params
    window.history.pushState({}, '', url)
    this.refresh()
  }

  _filterProducts(products) {
    return products.filter((product) =>
      this._filters.every((filter) => {
        switch (filter.facet) {
          case 'type':
            return filter.value.includes(product.type)
          case 'vendor':
            return filter.value.includes(product.vendor)
          case 'color':
            return filter.value.some(
              (_) =>
                product.options.includes(_) ||
                product.options.find(
                  (__) => __.name === 'Color' && __.values.includes(_)
                )
            )
          case 'price-min':
            return product.minPrice >= filter.value
          case 'price-max':
            return product.maxPrice <= filter.value
          case 'tags':
            return filter.value.some((_) => product.tags.includes(_))
          default:
            return false
        }
      })
    )
  }

  _renderActions() {
    return html`
      <div class="flex aic flex-grow-1">
        ${this.facetTagConfigurations.length
          ? html` <button
              class="c-subdued flex"
              id="toggle-filters-button"
              @click=${this.toggleFilters}
            >
              <span class="toggle-filters-button__filter">
                ${paths.filter}
              </span>
              <span class="toggle-filters-button__text">
                ${this.showingFilters ? 'Hide' : 'Show'} Filters
                ${this.filterCountText}
              </span>
            </button>`
          : ''}
        ${this.appliedFacets.length
          ? html`
              <div class="flex-grow-1 flex aic jcc" id="clear-filters-button">
                <button
                  @click=${this.clearFilters}
                  type="button"
                  class="clear-filters-button__button"
                >
                  Clear all
                </button>
              </div>
            `
          : ''}
      </div>
    `
  }

  render() {
    return html`
      ${this.facetTagConfigurations.length
        ? html` <div
            id="filters"
            @click=${this._handleFiltersClick}
            ${ref(this.$filters)}
            part="filters"
          >
            <hh-collection-filters
              .products=${this.products}
              .filters=${this._filters}
              .facetTagConfigurations=${this.facetTagConfigurations}
              @change=${this.refresh}
            ></hh-collection-filters>
          </div>`
        : ''}
      <div id="items">
        <!-- Optionally call this._renderAppliedFacets() here -->
        ${!this._loading && !this.filteredProducts.length
          ? html`
              <div id="empty-state">
                <p>No results.</p>
                <button
                  class="button button--outline"
                  @click=${this.clearFilters}
                >
                  Clear filters
                </button>
              </div>
            `
          : ''}
        ${this.normalizedStrategy === 'product'
          ? this._renderStrips()
          : this._renderGrid()}
        <footer id="footer">${this.renderPagination()}</footer>
      </div>
    `
  }

  _renderGrid() {
    return html`
      <div id="collection-page" class="grid container--xxl">
        ${this._loading
          ? html`<slot name="loading"></slot>`
          : html`
              ${repeat(
                this.currentPageItems,
                (product) =>
                  html`<hh-product-card
                    key=${product.id}
                    handle=${product.handle}
                    url=${product.url}
                    badge-text=${product.badgeText}
                    ?available=${product.available}
                    title=${product.title}
                    image-hover=${product.variants[0]?.metafields?.custom
                      ?.lifestyleImage ||
                    product.metafields?.custom?.lifestyleImage}
                    image=${product.image?.src}
                    compare-at-price=${product.compareAtPrice}
                    price=${product.price}
                    vendor=${product.vendor}
                    data-id=${product.id}
                    msrp=${product.variants[0] &&
                    product.variants[0].metafields.custom?.msrpPrice}
                    .options=${product.cardOptions}
                    .variants=${product.variants}
                    .swatches=${product?.swatchValues}
                    .rating=${product.rating}
                    ?hide-ratings=${product.hideReviews}
                  >
                    <div slot="ratings">
                      <div
                        data-bv-show="inline_rating"
                        data-bv-product-id=${product.id}
                        data-bv-redirect-url=${product.url}
                        data-bv-seo="false"
                      ></div>
                    </div>
                  </hh-product-card>`
              )}
            `}
      </div>
    `
  }

  _renderStrips() {
    return html`
      ${repeat(
        this.sortedProducts,
        (product) => html`
          <hh-product-strip
            key=${product.id}
            product-id=${product.id}
            url=${product.url}
            title=${product.title}
            sub-collection=${product?.subCollection}
            bundle=${product?.bundle}
            size-info=${product?.sizeInfo}
            description=${product?.tagLine}
            tag-info=${product?.tagInfo}
            link-text="View Details"
            .swatches=${product?.swatchValues}
            .variants=${product.variants}
            vendor=${product.vendor}
          >
            ${repeat(
              this._filterProducts(product.variants),
              (variant) => html`
                <hh-product-card
                  key=${product.id}
                  slot="product-card"
                  badge-text=${variant.badgeText}
                  image=${variant.image?.src}
                  title=${variant.title}
                  subtitle=${variant.options.join(' | ')}
                  id=${variant.id}
                  data-id=${variant.id}
                  url=${variant.url}
                  price=${variant.price}
                  image-hover=${variant?.metafields?.custom?.lifestyleImage ||
                  product.metafields?.custom?.lifestyleImage}
                  available=${variant.available}
                  compare-at-price=${variant.compareAtPrice}
                  msrp=${variant?.msrp}
                  .options=${variant.options}
                  .variants=${product.variants}
                  .swatches=${product?.swatchValues}
                  .rating=${product.rating}
                  ?hide-ratings=${product.hideReviews}
                >
                </hh-product-card>
              `
            )}
          </hh-product-strip>
        `
      )}
    `
  }

  static styles = [
    styles.grid,
    styles.flex,
    styles.reset,
    styles.typography,
    css`
      :host {
        display: block;
        margin: auto;
        min-height: 100vh;
        max-width: 1440px;
        padding: 0;
        --header-overlap-offset: 3px;
      }
      @media screen and (min-width: 992px) {
        :host {
          padding: 0 16px;
        }
      }
      #action-bar {
        align-items: center;
        background: #f5f5f5;
        justify-content: space-between;
        display: flex;
        position: sticky;
        z-index: 4;
        display: flex;
        align-items: center;
        padding: 1rem 0.875rem;
        top: calc(var(--action-bar-top-offset, var(--header-height)) - var(--header-overlap-offset));
        transition: top var(--action-bar-transition-timing, 0.6s) cubic-bezier(0, 0, 0.3, 1);
        transition-delay: var(--header-transition-delay);
      }
      @media screen and (min-width: 992px) {
        #action-bar {
          padding: 1rem 0.375rem;
        }
      }
      #actions {
        border-right: solid 1px rgb(var(--border-color));
        height: 100%;
        display: flex;
        width: 100%;
        justify-content: space-between;
      }
      #toggle-filters-button {
        background-color: transparent;
        cursor: pointer;
        display: block;
        height: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        font-family: Inter,sans-serif;
        font-weight: 500;
        font-size: 16px;
        line-height: 16px;
        padding: 0;
      }
      #collection-title {
        flex-grow: 1;
        font-family: PP Right Grotesk,sans-serif;
        font-size: 24px;
        font-weight: 500;
        line-height: 100%;
        margin: 0;
        display: none;
      }
      .collection-title__label {
        font-weight: 500;
        font-size: 24px;
        margin: 0;
      }
      #sort-dropdown {
        align-items: center;
        border-left: solid 1px rgb(var(--border-color));
        height: 100%;
        display: flex;
        width: max-content;
      }
      #applied-facets {
        flex-wrap: wrap;
        align-items: center;
        gap: 0.625rem;
        display: flex;
        padding: 1rem 1rem 0;
      }
      .clear-filters-button__button {
        background-color: transparent;
        cursor: pointer;
        text-decoration: underline;
        font-size: 16px;
        color: var(--black);
        font-weight: 500;
        padding: 0;
      }
      .removable-facet {
        font-size: var(--text-sm);
        border-width: 1px;
        align-items: center;
        column-gap: 0.375rem;
        padding: 0.375rem 0.625rem;
        display: flex;
      }
      .remove-facet-button {
        width: 20px;
      }
      .remove-facet-button svg {
        width: 100%;
      }
      .card-skeleton {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      #empty-state {
        align-items: center;
        display: flex;
        flex-direction: column;
        gap: 32px;
        justify-content: center;
        height: 80vh;
        width: 100%;
      }
      #collection-page {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      #pagination {
        align-items: center;
        border-block-end-width: 1px;
        display: inline-grid;
        grid-auto-flow: column;
        justify-self: center;
        gap: 5px;
        grid-auto-rows: minmax(0,2.8125rem);
        grid-auto-columns: minmax(0,2.625rem);
      }
      #pagination > li {
        place-content: center;
        display: grid;
        position: relative;
      }
      .page-button {
        background-color: transparent;
        cursor: pointer;
        width: 100%;
        height: 100%;
      }
      .page-button--prev {
        transform: scaleX(-100%);
      }
      .page-button[active] {
        box-shadow: 0 1px, inset 0 -2px;
      }
      #filters {
        background-color: #f5f5f5;
        display: flex;
        height: auto;
        left: 0;
        max-height: 0;
        max-width: unset;
        overflow: hidden;
        padding: 0;
        position: sticky;
        --header-overlap-offset: 5px;
        top: calc(var(--action-bar-height, 174px) + var(--action-bar-top-offset) - var(--header-overlap-offset));
        transform: translateX(0);
        transition: max-height 0.3s ease-out, top var(--action-bar-transition-timing, 0.6s) cubic-bezier(0, 0, 0.3, 1);
        z-index: 3;
        transition-delay: var(--header-transition-delay);
      }
      #filters header {
        display: flex;
        justify-content: space-between;
        padding: 16px;
      }
      .filters__close {
        width: 26px;
      }
      #filters hh-collection-filters {
        overflow: scroll;
        scrollbar-width: none;
      }
      :host([showing-filters]) #filters {
        transform: translateX(0);
      }
      #footer {
        align-items: center;
        padding: 3rem 0 4rem;
        display: flex;
        justify-content: center;
      }
      .toggle-filters-button__text {
        font-weight: 500;
        color: var(--black);
      }
      .toggle-filters-button__filter {
        color: var(--black);
      }
      @media screen and (min-width: 450px) {
        #collection-page {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
      @media screen and (min-width: 700px) {
        .removable-facet {
          font-size: var(--text-base);
          column-gap: 0.625rem;
          padding: 0.625rem 0.875rem;
        }
        #collection-page {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
        #pagination {
          grid-auto-columns: minmax(0,3.75rem);
        }
      }
      @media screen and (min-width: 992px) {
        #collection-page {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }
        #actions {
          width: unset;
          justify-content: unset;
          gap: 26px;
        }
        #toggle-filters-button {
          font-size: 14px;
        }
        .toggle-filters-button__text {
          order: -1;
        }
        #collection-title {
          display: block;
        }
        #clear-filters-button {
          order: -1;
        }
        .clear-filters-button__button {
          font-size: 14px;
          margin-right: 8px;
        }
    `,
  ]
}

customElements.define('hh-collection-grid', HHCollectionGrid)
